/**
 * ============================================================
 * KEJJA OS — src/pages/pantry/PantryListPage.tsx
 * ============================================================
 */

import { useState } from 'react';
import {
  Plus, Search, Minus, PlusCircle, AlertTriangle, Clock, Trash2,
  Pencil, Package,
} from 'lucide-react';
import {
  usePantryContext, CATEGORIES, LOCATIONS,
} from './PantryLayout';
import {
  usePantryItems, useAdjustPantry, useDeletePantryItem,
} from '@/hooks/usePantry';
import type { PantryItem } from '@/lib/pantryApi';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import PantryItemForm from './PantryItemForm';

export default function PantryListPage() {
  const { homeId } = usePantryContext();
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | ''>('');
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState<PantryItem | null>(null);

  const { data, isLoading } = usePantryItems(homeId, {
    search: search.trim() || undefined,
    category: category || undefined,
  });

  const items = data?.items ?? [];

  // Group by category for display
  const grouped = new Map<string, PantryItem[]>();
  for (const it of items) {
    const arr = grouped.get(it.category) ?? [];
    arr.push(it);
    grouped.set(it.category, arr);
  }

  return (
    <div className="space-y-4 pb-16">
      <div className="flex gap-2">
        <div className="flex-1 relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search pantry…"
            className="pl-8"
          />
        </div>
        <Button
          size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-shrink-0"
          onClick={() => { setEditingItem(null); setShowForm(true); }}
        >
          <Plus className="w-4 h-4 mr-1" /> Add
        </Button>
      </div>

      <div className="flex gap-1 overflow-x-auto pb-1">
        <CategoryChip label="All" value="" current={category} onClick={setCategory} emoji="🗂️" />
        {CATEGORIES.map((c) => (
          <CategoryChip
            key={c.value} label={c.label} value={c.value}
            current={category} onClick={setCategory} emoji={c.emoji}
          />
        ))}
      </div>

      {isLoading ? (
        <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
      ) : items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-8 text-center">
            <Package className="w-10 h-10 text-muted-foreground/40 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {search || category ? 'No items match.' : 'Pantry is empty. Add your first item.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Array.from(grouped.entries()).map(([cat, list]) => {
            const catDef = CATEGORIES.find((c) => c.value === cat);
            return (
              <div key={cat}>
                <h3 className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                  <span>{catDef?.emoji ?? '📦'}</span>
                  {catDef?.label ?? cat}
                  <span className="ml-1 text-muted-foreground/60">({list.length})</span>
                </h3>
                <Card>
                  <CardContent className="p-0 divide-y divide-border">
                    {list.map((it) => (
                      <PantryRow
                        key={it.id}
                        item={it}
                        homeId={homeId}
                        onEdit={() => { setEditingItem(it); setShowForm(true); }}
                      />
                    ))}
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <PantryItemForm
          homeId={homeId}
          item={editingItem}
          onClose={() => { setShowForm(false); setEditingItem(null); }}
        />
      )}
    </div>
  );
}

function CategoryChip({
  label, value, current, onClick, emoji,
}: {
  label: string; value: string; current: string; onClick: (v: string) => void; emoji: string;
}) {
  const on = current === value;
  return (
    <button
      onClick={() => onClick(value)}
      className={cn(
        'flex-shrink-0 flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-medium transition-colors',
        on
          ? 'border-emerald-500/60 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
          : 'border-border text-muted-foreground hover:bg-muted',
      )}
    >
      <span>{emoji}</span><span>{label}</span>
    </button>
  );
}

// ============================================================
// PantryRow
// ============================================================

function PantryRow({ item, homeId, onEdit }: {
  item: PantryItem; homeId: string; onEdit: () => void;
}) {
  const adjustMut = useAdjustPantry(homeId);
  const deleteMut = useDeletePantryItem(homeId);
  const loc = LOCATIONS.find((l) => l.value === item.location);

  return (
    <div className={cn(
      'p-3 flex items-center gap-3',
      item.isOutOfStock && 'bg-rose-50/40 dark:bg-rose-950/10',
      item.isLowStock && !item.isOutOfStock && 'bg-amber-50/40 dark:bg-amber-950/10',
    )}>
      <div className="h-9 w-9 rounded-lg bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center flex-shrink-0 text-lg">
        {item.emoji ?? '📦'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm truncate">{item.name}</span>
          {item.isOutOfStock && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 border-rose-500/60 text-rose-700 dark:text-rose-300">
              <AlertTriangle className="w-2 h-2 mr-0.5" /> Out
            </Badge>
          )}
          {item.isLowStock && !item.isOutOfStock && (
            <Badge variant="outline" className="text-[9px] h-4 px-1 border-amber-500/60 text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-2 h-2 mr-0.5" /> Low
            </Badge>
          )}
          {item.isExpiringSoon && item.daysUntilExpiry !== null && (
            <Badge variant="outline" className={cn(
              'text-[9px] h-4 px-1',
              item.daysUntilExpiry < 0
                ? 'border-rose-500/60 text-rose-700 dark:text-rose-300'
                : 'border-orange-500/60 text-orange-700 dark:text-orange-300',
            )}>
              <Clock className="w-2 h-2 mr-0.5" />
              {item.daysUntilExpiry < 0
                ? `Expired ${Math.abs(item.daysUntilExpiry)}d ago`
                : item.daysUntilExpiry === 0 ? 'Expires today'
                : `${item.daysUntilExpiry}d left`}
            </Badge>
          )}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
          <span className="tabular-nums font-semibold">{item.quantity} {item.unit}</span>
          {item.minStockLevel !== null && (
            <span className="opacity-70">· min {item.minStockLevel}</span>
          )}
          {loc && <span>· {loc.emoji} {loc.label}</span>}
        </div>
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button
          onClick={() => adjustMut.mutate({ itemId: item.id, delta: -1 })}
          className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground flex items-center justify-center"
          aria-label="Decrease"
        >
          <Minus className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={() => adjustMut.mutate({ itemId: item.id, delta: 1 })}
          className="h-7 w-7 rounded-md hover:bg-muted text-emerald-700 dark:text-emerald-300 flex items-center justify-center"
          aria-label="Increase"
        >
          <PlusCircle className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onEdit}
          className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground flex items-center justify-center"
        >
          <Pencil className="w-3 h-3" />
        </button>
        <button
          onClick={() => {
            if (confirm(`Delete "${item.name}"?`)) deleteMut.mutate(item.id);
          }}
          className="h-7 w-7 rounded-md hover:bg-muted text-muted-foreground hover:text-rose-600 flex items-center justify-center"
        >
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
